import React, { createContext, useContext, useState, useEffect, useRef, ReactNode } from 'react';
import { useOktaAuth } from '@okta/okta-react';

export interface ActiveValidation {
  jobKey: string;
  jobUrl: string;
  startedAt: number; // Date.now() when triggered
  repoName: string;
  folder: string;
  provider?: 'aws' | 'azure' | 'gcp';
}

interface ValidationContextType {
  activeValidation: ActiveValidation | null;
  setActiveValidation: (v: ActiveValidation | null) => void;
}

const ValidationContext = createContext<ValidationContextType | undefined>(undefined);

function readSession<T>(key: string, fallback: T): T {
  try {
    const raw = localStorage.getItem(key);
    return raw ? (JSON.parse(raw) as T) : fallback;
  } catch {
    return fallback;
  }
}

export const ValidationProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const { authState, oktaAuth } = useOktaAuth();
  const [userId, setUserId] = useState('');

  useEffect(() => {
    if (authState?.isAuthenticated) {
      oktaAuth.getUser().then((u) => {
        if (u?.sub) setUserId(u.sub);
      }).catch(() => {});
    } else if (authState && !authState.isAuthenticated) {
      setUserId('');
    }
  }, [authState, oktaAuth]);

  const [activeValidation, _setActiveValidation] = useState<ActiveValidation | null>(null);

  // Re-hydrate from sessionStorage once userId is known
  const prevUserIdRef = useRef('');
  useEffect(() => {
    if (!userId || userId === prevUserIdRef.current) return;
    prevUserIdRef.current = userId;
    const key = `iac:${userId}:active-validation`;
    const stored = readSession<ActiveValidation | null>(key, null);
    _setActiveValidation(stored);
  }, [userId]);

  const setActiveValidation = (v: ActiveValidation | null) => {
    _setActiveValidation(v);
    if (!userId) return;
    const key = `iac:${userId}:active-validation`;
    if (v === null) {
      localStorage.removeItem(key);
    } else {
      try {
        localStorage.setItem(key, JSON.stringify(v));
      } catch { /* quota — lives in memory only */ }
    }
  };

  return (
    <ValidationContext.Provider value={{ activeValidation, setActiveValidation }}>
      {children}
    </ValidationContext.Provider>
  );
};

export const useValidation = (): ValidationContextType => {
  const ctx = useContext(ValidationContext);
  if (!ctx) throw new Error('useValidation must be used within ValidationProvider');
  return ctx;
};
